if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopCustomMql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener('click', (event) => {
            if (this.isDesktopCustomGallery()) {
              event.preventDefault();
              this.setActiveMediaInDesktopScroll(mediaToSwitch.dataset.target, true);
              return;
            }

            this.setActiveMedia(mediaToSwitch.dataset.target, false);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.initDesktopScrollGallery();
      }

      isDesktopCustomGallery() {
        return Boolean(
          this.desktopCustomMql.matches &&
            this.dataset.desktopLayout &&
            this.dataset.desktopLayout.includes('thumbnail') &&
            this.elements.viewer &&
            this.elements.thumbnails
        );
      }

      initDesktopScrollGallery() {
        if (!this.elements.viewer || !this.elements.thumbnails) return;

        this.desktopScrollContainer = this.elements.viewer.querySelector('.product__media-list');
        if (!this.desktopScrollContainer) return;

        this.desktopScrollTicking = false;

        this.desktopScrollContainer.addEventListener(
          'scroll',
          () => {
            if (!this.isDesktopCustomGallery()) return;
            if (this.desktopScrollTicking) return;

            this.desktopScrollTicking = true;
            window.requestAnimationFrame(() => {
              this.syncDesktopActiveMediaFromScroll();
              this.desktopScrollTicking = false;
            });
          },
          { passive: true }
        );

        this.desktopCustomMql.addEventListener?.('change', () => {
          if (this.isDesktopCustomGallery()) {
            this.syncDesktopActiveMediaFromScroll();
          }
        });

        window.setTimeout(() => {
          if (this.isDesktopCustomGallery()) this.syncDesktopActiveMediaFromScroll();
        }, 150);
      }

      getDesktopMediaItems() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter((item) => {
          if (item.classList.contains('product__media-item--variant') && item.offsetParent === null) return false;
          return true;
        });
      }

      syncDesktopActiveMediaFromScroll() {
        const scrollContainer = this.desktopScrollContainer || this.elements.viewer.querySelector('.product__media-list');
        if (!scrollContainer) return;

        const mediaItems = this.getDesktopMediaItems();
        if (!mediaItems.length) return;

        const containerRect = scrollContainer.getBoundingClientRect();
        const targetY = containerRect.top + containerRect.height / 2;

        let closestMedia = mediaItems[0];
        let closestDistance = Number.POSITIVE_INFINITY;

        mediaItems.forEach((media) => {
          const rect = media.getBoundingClientRect();
          const mediaCenter = rect.top + rect.height / 2;
          const distance = Math.abs(mediaCenter - targetY);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestMedia = media;
          }
        });

        if (!closestMedia?.dataset?.mediaId) return;
        this.markDesktopActiveMedia(closestMedia.dataset.mediaId);
      }

      markDesktopActiveMedia(mediaId) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail, true);
        this.playActiveMedia(activeMedia);
      }

      setActiveMediaInDesktopScroll(mediaId, smooth = true) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const scrollContainer = this.desktopScrollContainer || this.elements.viewer.querySelector('.product__media-list');

        if (!activeMedia || !scrollContainer) return;

        const top = activeMedia.offsetTop - scrollContainer.offsetTop;
        scrollContainer.scrollTo({
          top,
          behavior: smooth ? 'smooth' : 'auto',
        });

        this.markDesktopActiveMedia(mediaId);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.isDesktopCustomGallery() && !prepend) {
          this.setActiveMediaInDesktopScroll(mediaId, true);
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.isDesktopCustomGallery()) {
            this.setActiveMediaInDesktopScroll(mediaId, false);
            return;
          }

          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail, verticalMode = false) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (verticalMode) {
          const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list') || this.elements.thumbnails;
          const top = thumbnail.offsetTop - thumbnailList.offsetTop;
          thumbnailList.scrollTo?.({ top, behavior: 'smooth' });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
